import { useState, useEffect, useCallback } from 'react';
import {
  Palette,
  Image,
  Building2,
  Save,
  RefreshCw,
  Upload,
  Check,
  AlertCircle,
  Eye,
} from 'lucide-react';
import { Button, Card, Spinner, useToast } from '@centraoneplus/ui';
import { tenantService } from '@services/tenant.service';
import { useAuthStore } from '@stores/authStore';
import { useTenantContextStore } from '@stores/tenantContextStore';
import type { Branding } from '@/types/tenant';

// Preset color palettes
const COLOR_PRESETS = [
  { name: 'Blue', primary: '#3B82F6', secondary: '#1E40AF' },
  { name: 'Indigo', primary: '#6366F1', secondary: '#4338CA' },
  { name: 'Purple', primary: '#8B5CF6', secondary: '#6D28D9' },
  { name: 'Pink', primary: '#EC4899', secondary: '#BE185D' },
  { name: 'Red', primary: '#EF4444', secondary: '#B91C1C' },
  { name: 'Orange', primary: '#F97316', secondary: '#C2410C' },
  { name: 'Yellow', primary: '#EAB308', secondary: '#A16207' },
  { name: 'Green', primary: '#22C55E', secondary: '#15803D' },
  { name: 'Teal', primary: '#14B8A6', secondary: '#0F766E' },
  { name: 'Cyan', primary: '#06B6D4', secondary: '#0E7490' },
  { name: 'Gray', primary: '#6B7280', secondary: '#374151' },
  { name: 'Slate', primary: '#64748B', secondary: '#334155' },
];

export default function BrandingSettings() {
  const toast = useToast();
  const { user } = useAuthStore();
  const { currentTenant } = useTenantContextStore();
  const tenantId = currentTenant?.id || user?.tenantId;

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [branding, setBranding] = useState<Branding>({
    primaryColor: '#3B82F6',
    logoUrl: '',
    faviconUrl: '',
    companyName: '',
  });

  const [originalBranding, setOriginalBranding] = useState<Branding | null>(null);

  // Load branding
  const loadBranding = useCallback(async () => {
    if (!tenantId) return;

    try {
      setIsLoading(true);
      const data = await tenantService.getBranding(tenantId);
      setBranding(data);
      setOriginalBranding(data);
    } catch (error) {
      console.error('Error loading branding:', error);
      toast.error('Failed to load branding settings');
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, toast]);

  useEffect(() => {
    loadBranding();
  }, [loadBranding]);

  // Track changes
  useEffect(() => {
    if (originalBranding) {
      const changed =
        branding.primaryColor !== originalBranding.primaryColor ||
        branding.logoUrl !== originalBranding.logoUrl ||
        branding.faviconUrl !== originalBranding.faviconUrl ||
        branding.companyName !== originalBranding.companyName;
      setHasChanges(changed);
    }
  }, [branding, originalBranding]);

  // Save branding
  const handleSave = async () => {
    if (!tenantId) return;

    try {
      setIsSaving(true);
      await tenantService.updateBranding(tenantId, branding);
      setOriginalBranding(branding);
      setHasChanges(false);
      toast.success('Branding settings saved');
    } catch (error) {
      console.error('Error saving branding:', error);
      toast.error('Failed to save branding settings');
    } finally {
      setIsSaving(false);
    }
  };

  // Reset to original
  const handleReset = () => {
    if (originalBranding) {
      setBranding(originalBranding);
    }
  };

  // Handle color change
  const handleColorChange = (color: string) => {
    setBranding({ ...branding, primaryColor: color });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Branding & Appearance</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Customize the look and feel of your help desk
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => setShowPreview(!showPreview)}>
            <Eye className="h-4 w-4 mr-2" />
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </Button>
        </div>
      </div>

      <div className={`grid grid-cols-1 gap-6 ${showPreview ? 'lg:grid-cols-2' : ''}`}>
        {/* Settings Panel */}
        <div className="space-y-6">
          {/* Company Name */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <Building2 className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">Company Name</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Displayed in emails and the portal</p>
              </div>
            </div>
            <input
              type="text"
              value={branding.companyName || ''}
              onChange={(e) => setBranding({ ...branding, companyName: e.target.value })}
              placeholder="Your Company Name"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </Card>

          {/* Primary Color */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <Palette className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">Primary Color</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Used for buttons, links, and accents</p>
              </div>
            </div>

            {/* Color picker */}
            <div className="flex items-center gap-4 mb-4">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={branding.primaryColor || '#3B82F6'}
                  onChange={(e) => handleColorChange(e.target.value)}
                  className="w-10 h-10 rounded cursor-pointer border border-gray-300 dark:border-gray-600"
                />
                <input
                  type="text"
                  value={branding.primaryColor || '#3B82F6'}
                  onChange={(e) => handleColorChange(e.target.value)}
                  placeholder="#3B82F6"
                  className="w-28 px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Color presets */}
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Quick presets</p>
              <div className="flex flex-wrap gap-2">
                {COLOR_PRESETS.map((preset) => (
                  <button
                    key={preset.name}
                    onClick={() => handleColorChange(preset.primary)}
                    className={`w-8 h-8 rounded-lg border-2 transition-all ${
                      branding.primaryColor === preset.primary
                        ? 'border-gray-900 dark:border-white scale-110'
                        : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: preset.primary }}
                    title={preset.name}
                  >
                    {branding.primaryColor === preset.primary && (
                      <Check className="h-4 w-4 text-white mx-auto" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </Card>

          {/* Logo */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <Image className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">Logo</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Displayed in the header and emails</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-4">
                {branding.logoUrl ? (
                  <div className="w-32 h-16 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center justify-center p-2">
                    <img
                      src={branding.logoUrl}
                      alt="Logo preview"
                      className="max-w-full max-h-full object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                ) : (
                  <div className="w-32 h-16 bg-gray-50 dark:bg-gray-800 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center">
                    <Image className="h-6 w-6 text-gray-300" />
                  </div>
                )}
              </div>

              <input
                type="url"
                value={branding.logoUrl || ''}
                onChange={(e) => setBranding({ ...branding, logoUrl: e.target.value })}
                placeholder="https://example.com/logo.png"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Enter the URL of your logo image. Recommended size: 200x50 pixels.
              </p>
            </div>
          </Card>

          {/* Favicon */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <Image className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">Favicon</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Browser tab icon</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-4">
                {branding.faviconUrl ? (
                  <div className="w-10 h-10 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center justify-center p-1">
                    <img
                      src={branding.faviconUrl}
                      alt="Favicon preview"
                      className="max-w-full max-h-full object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                ) : (
                  <div className="w-10 h-10 bg-gray-50 dark:bg-gray-800 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center">
                    <Image className="h-4 w-4 text-gray-300" />
                  </div>
                )}
              </div>

              <input
                type="url"
                value={branding.faviconUrl || ''}
                onChange={(e) => setBranding({ ...branding, faviconUrl: e.target.value })}
                placeholder="https://example.com/favicon.ico"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Enter the URL of your favicon. Recommended size: 32x32 pixels.
              </p>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4">
            {hasChanges && (
              <p className="text-sm text-amber-600 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                You have unsaved changes
              </p>
            )}
            <div className="flex items-center gap-3 ml-auto">
              <Button variant="outline" onClick={handleReset} disabled={!hasChanges || isSaving}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Reset
              </Button>
              <Button onClick={handleSave} disabled={!hasChanges || isSaving}>
                {isSaving ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Preview Panel */}
        {showPreview && (
          <div className="lg:sticky lg:top-6 space-y-6">
            <Card className="overflow-hidden">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">Live Preview</h3>
              </div>

              {/* Mock Header Preview */}
              <div
                className="p-4 flex items-center gap-4"
                style={{ backgroundColor: branding.primaryColor || '#3B82F6' }}
              >
                {branding.logoUrl ? (
                  <img
                    src={branding.logoUrl}
                    alt="Logo"
                    className="h-8 object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <span className="text-white font-semibold">
                    {branding.companyName || 'Your Company'}
                  </span>
                )}
              </div>

              {/* Mock Content */}
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: branding.primaryColor || '#3B82F6' }}
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-400">Sample navigation item</span>
                </div>

                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Welcome to the Help Center</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  This is a preview of how your branding will appear to customers.
                </p>

                <button
                  className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                  style={{ backgroundColor: branding.primaryColor || '#3B82F6' }}
                >
                  Sample Button
                </button>

                <a
                  href="#"
                  className="block text-sm font-medium"
                  style={{ color: branding.primaryColor || '#3B82F6' }}
                  onClick={(e) => e.preventDefault()}
                >
                  Sample Link
                </a>
              </div>

              {/* Mock Footer */}
              <div className="p-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Powered by {branding.companyName || 'Your Company'}
                </p>
              </div>
            </Card>

            {/* Browser Tab Preview */}
            <Card className="p-4">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Browser Tab Preview</h4>
              <div className="bg-gray-100 dark:bg-gray-700 rounded-t-lg p-2">
                <div className="flex items-center gap-2 bg-white dark:bg-gray-800 rounded px-3 py-1.5 max-w-xs">
                  {branding.faviconUrl ? (
                    <img
                      src={branding.faviconUrl}
                      alt=""
                      className="w-4 h-4 object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-4 h-4 bg-gray-200 dark:bg-gray-600 rounded" />
                  )}
                  <span className="text-xs text-gray-700 dark:text-gray-300 truncate">
                    {branding.companyName || 'Help Center'}
                  </span>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
